import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, effect, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormMode } from 'src/app/controls/common/form-mode';
import { ContextOfUse } from 'src/app/models/RestApiCaller/Enums/context-of-use';
import { RestApiResponseFormatType } from 'src/app/models/RestApiCaller/Enums/rest-api-response-format-type';
import { RestApiResponseConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-response-configuration';
import { ApiConfigurationDetailContext } from '../../api-configuration-detail-context';
import { ApiConfigurationState } from '../models/api-configuration-state';
import { VariablesFormComponent } from './variables-form/variables-form.component';

@Component({
    selector: 'app-api-configuration-response',
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule, MatButtonModule, MatInputModule, VariablesFormComponent],
    templateUrl: './api-configuration-response.component.html',
    styleUrl: './api-configuration-response.component.scss',
})
export class ApiConfigurationResponseComponent implements OnInit {
    /**
     * Etat de la configuration d'API
     */
    protected readonly apiConfigurationState = inject(ApiConfigurationState);

    /**
     * Liste des enums utilisés dans le template.
     */
    public FormMode = FormMode;
    public ContextOfUse = ContextOfUse;

    /**
     * Formulaire contenant les informations relatives à la réponse de la configuration d'API.
     */
    public apiConfigurationResponseForm: FormGroup;

    /**
     * Liste des types de format de requête et leur libellé.
     */
    public responseFormatTypes: Map<RestApiResponseFormatType, string> = new Map<RestApiResponseFormatType, string>();

    /**
     * Contexte de la configuration de l'API.
     */
    @Input() context: ApiConfigurationDetailContext;

    /**
     * Constructeur.
     */
    constructor() {
        effect(() => {
            if (this.apiConfigurationState.$mode() === FormMode.Reading) {
                this.apiConfigurationResponseForm.disable();
            } else {
                this.apiConfigurationResponseForm.enable();
            }
        });
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit(): void {
        if (this.context.apiConfigurationContext.restApiConfiguration.contextOfUse == ContextOfUse.WorkflowAction) {
            this.responseFormatTypes.set(RestApiResponseFormatType.Text, 'Texte brut');
        }

        this.responseFormatTypes.set(RestApiResponseFormatType.Json, 'JSON');
        this.responseFormatTypes.set(RestApiResponseFormatType.Xml, 'XML');

        this.apiConfigurationResponseForm = new FormGroup({
            responseFormatType: new FormControl(''),
            responseVariableConfigurations: new UntypedFormControl([]),
            datasourcePath: new UntypedFormControl(''),
        });

        this.fillForm();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public onCancelClick() {
        this.fillForm();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Enregistrer'.
     */
    onSaveClick() {
        let apiConfiguration = this.apiConfigurationState.$apiConfiguration().createCopy();

        if (!apiConfiguration.responseConfiguration) {
            apiConfiguration.responseConfiguration = new RestApiResponseConfiguration();
        }

        apiConfiguration.responseConfiguration.responseFormatType = this.apiConfigurationResponseForm.get('responseFormatType').value;

        if (this.context.apiConfigurationContext.restApiConfiguration.contextOfUse == ContextOfUse.WorkflowAction) {
            apiConfiguration.responseConfiguration.responseVariableConfigurations = this.apiConfigurationResponseForm.get('responseVariableConfigurations').value;
        } else {
            apiConfiguration.responseConfiguration.datasourcePath = this.apiConfigurationResponseForm.get('datasourcePath').value;
        }

        this.apiConfigurationState.$apiConfiguration.set(apiConfiguration);
    }

    /**
     * Remplit les champs du formulaire avec les données de la configuration d'API.
     */
    private fillForm() {
        this.apiConfigurationResponseForm.get('responseFormatType').setValue(this.apiConfigurationState.$apiConfiguration().responseConfiguration?.responseFormatType);

        if (this.context.apiConfigurationContext.restApiConfiguration.contextOfUse == ContextOfUse.WorkflowAction) {
            this.apiConfigurationResponseForm
                .get('responseVariableConfigurations')
                .setValue(this.apiConfigurationState.$apiConfiguration().responseConfiguration?.responseVariableConfigurations);
        } else {
            this.apiConfigurationResponseForm.get('datasourcePath').setValue(this.apiConfigurationState.$apiConfiguration().responseConfiguration?.datasourcePath);
        }
    }
}
